import Organization, { OrganizationAPIResponse } from "@/classes/organization";
import APIProperty from "@/api/auth/property";
import APIStation from "@/api/auth/station";
import APICluster from "@/api/auth/cluster";
import keyBy from "lodash/keyBy";
import { DATA_API_SERVICE } from "@/appConfig";
import { AppAbilityChecker } from "@/appAbility";
import { UserStore } from "@/store";
import sortBy from "lodash/sortBy";
import API from "@/api/api";
import apolloClient from "@/api/charge";
import { gql } from "@apollo/client/core";

export function canViewOrganizationCheck(organization: Organization) {
  if (AppAbilityChecker.canViewAsAdmin()) return true;
  if (UserStore.profile.organization) {
    return UserStore.profile.organization === organization.id;
  }
  return true;
}

async function removeOrganizationById(organizationId: string) {
  return await API.delete(`${DATA_API_SERVICE}/organization/${organizationId}`);
}

async function createOrganization(organization: Organization) {
  const response = await API.post<OrganizationAPIResponse>(`${DATA_API_SERVICE}/organization`, organization.toJson());
  return response.data.id;
}

async function updateOrganization(organization: Organization) {
  return await API.put<OrganizationAPIResponse>(
    `${DATA_API_SERVICE}/organization/${organization.id}`,
    organization.toJson()
  );
}

async function archiveOrganization(organizationId: string) {
  return await API.delete(`${DATA_API_SERVICE}/organization/${organizationId}`);
}

async function findAll() {
  const res = await apolloClient.query({
    query: gql`
      query Organizations {
        organizations {
          id
          name
          address1
          address2
          city
          state
          zip
          phone
          email
          manager: contact
          note
        }
      }
    `
  });
  const organizationsResponse = (res.data?.organizations || []) as OrganizationAPIResponse[];
  const organizations = organizationsResponse.map(organization => Organization.fromAPIResponse(organization));
  return organizations.filter(organization => {
    return canViewOrganizationCheck(organization);
  });
}

async function findAllWithFullTree(): Promise<Organization[]> {
  const [allOrganizations, allProperties, allStations, allClusters] = await Promise.all([
    findAll(),
    APIProperty.findAll(),
    APIStation.findAll(),
    APICluster.findAll()
  ]);
  await APIStation.mapStationsToDisconnectStatus(allStations);
  const clusterLookup = keyBy(allClusters, cluster => cluster.id);
  allStations.forEach(station => {
    station.cluster = clusterLookup[station.clusterId];
  });
  allProperties.forEach(property => {
    property.stations = allStations.filter(station => station.propertyId === property.id);
  });
  allOrganizations.forEach(organization => {
    organization.properties = allProperties.filter(property => property.oKey === organization.id);
  });
  return sortBy(allOrganizations, organization => organization.name);
}

async function findById(organizationId: string) {
  const res = await apolloClient.query({
    query: gql`
      query Organization($filter: OrganizationFilter) {
        organization(filter: $filter) {
          id
          name
          address1
          address2
          city
          state
          zip
          phone
          email
          manager: contact
          note
        }
      }
    `,
    variables: {
      filter: {
        id: organizationId
      }
    }
  });
  const organization = Organization.fromAPIResponse(res.data?.organization);
  if (!canViewOrganizationCheck(organization)) throw new Error("Unauthorized. Cannot view organization");
  return organization;
}

async function getPropertiesByOrganizationId(organizationId: string) {
  const organizations = await findAllWithFullTree();
  const organization = organizations.find(organization => organization.id === organizationId);
  if (!organization) throw new Error(`Organization not found: ${organizationId}`);
  return {
    organization,
    properties: organization.properties
  };
}

const APIOrganization = {
  createOrganization,
  archiveOrganization,
  findAll,
  findById,
  findAllWithFullTree,
  getPropertiesByOrganizationId,
  removeOrganizationById,
  updateOrganization
};

export default APIOrganization;
